import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { useDateiUrl } from '../hooks/useDateiUrl';
import { UnterschriftenCheckboxState } from '../screens/Vorgang/Unterschriften';
import '../screens/Vorgang/Unterschriften.css';
import { Unterzeichner } from '../shared/constants';
import { Vorgang } from '../types';
import { Signature } from './common/Signature';
import { SPACING_BETWEEN_FORM_FIELDS } from './common/spacings';
import { Modal } from './Modal';
import { MEHRWERTSTEUER } from '../shared/domain/vorgang';

type Props = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly updateCheckboxState: (newState: Partial<UnterschriftenCheckboxState>) => void;
  readonly checkboxState: UnterschriftenCheckboxState;
};

export function FahrtkostenvereinbarungReparaturbestaetigung({ vorgang, isLoading, updateCheckboxState, checkboxState }: Props): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxState.fahrtkostenvereinbarungReparaturnachweis}
              onChange={(event: ChangeEvent<HTMLInputElement>) => updateCheckboxState({ fahrtkostenvereinbarungReparaturnachweis: event.target.checked })}
              name="fahrtkostenvereinbarungGutachten"
              color="primary"
              data-testid="fahrtkostenvereinbarungGutachtenCheckbox"
            />
          }
          label={
            <>
              Ich habe die AGB{' '}
              <Link
                onClick={(event) => {
                  event.preventDefault();
                  setIsModalOpen(true);
                }}
                data-testid="fahrtkostenvereinbarungReparaturbestaetigungLink"
              >
                Fahrtkostenvereinbarung zur Reparaturbestätigung
              </Link>{' '}
              zur Kenntnis genommen und bin mit der Geltung einverstanden.
            </>
          }
        />
      </Grid>
      {isModalOpen && (
        <FahrtkostenvereinbarungReparaturbestaetigungModal
          vorgang={vorgang}
          isLoading={isLoading}
          isModalOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

type ModalProps = {
  readonly vorgang: Vorgang;
  readonly isLoading: boolean;
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
};

function FahrtkostenvereinbarungReparaturbestaetigungModal({ vorgang, isLoading, isModalOpen, onClose }: ModalProps): JSX.Element {
  const url = useDateiUrl(vorgang?.unterschriften?.fahrtkostenvereinbarungReparaturnachweis?.key);

  const signDate = vorgang?.unterschriften?.fahrtkostenvereinbarungReparaturnachweis?.datum ?? new Date().toISOString();

  return (
    <Modal
      openModal={isModalOpen}
      setOpenModal={onClose}
      title="Fahrtkostenvereinbarung zur Reparaturbestätigung"
      body={
        <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} data-testid="fahrtkostenModal">
          <Grid size={12}>
            <Typography variant="h6">§1 Geltung der Bedingungen für die Anfahrt und Abfahrt</Typography>
            <Typography variant="body2">
              Die An- und Abfahrt zur Besichtigung zur Reparaturbestätigung durch den Auftragnehmer (AN) zum Auftraggeber (AG) erfolgt ausschließlich aufgrund
              dieser Geschäftsbedingungen.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§2 Abschluss der Vereinbarung</Typography>
            <Typography variant="body2">
              Das Vertragsangebot erfolgt regelmäßig durch (auch digitale) Unterschrift des Auftraggebers unter das Auftragsformular und Zugang des
              unterschriebenen Auftragsformulars beim AN.
            </Typography>
            <Typography variant="body2">
              Der Auftrag kann alternativ auch im Wege der elektronischen Auftragserteilung zustande kommen. In diesem Fall gibt der AG ein verbindliches
              Angebot ab, indem er
            </Typography>
            <Typography variant="body2">
              - die im Beauftragungsprozess bereitgestellten Vertragsbedingungen durch Anklicken der hierfür vorgesehenen Check-Boxen akzeptiert und
            </Typography>
            <Typography variant="body2">
              - den Auftrag durch Betätigung des eindeutig beschrifteten Buttons „kostenpflichtig bestellen“ bzw. „kostenpflichtig beauftragen“ absendet.
            </Typography>
            <Typography variant="body2">
              Die Auftragsannahme erfolgt durch Erklärung des AN gegenüber dem AG, insbesondere durch elektronische Bestätigung der Auftragsannahme.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography variant="h6">§3 Vergütung des Sachverständigen für die An- und Abfahrt zur Reparaturbestätigung</Typography>
            <Typography variant="body2">
              (1) Der AN und der AG vereinbaren An- und Abfahrtskosten zur Besichtigung zur Reparaturbestätigung in Höhe von 0,83 € netto (0,99 € brutto inkl.{' '}
              {MEHRWERTSTEUER}% gesetzlicher Mehrwertsteuer) pro gefahrenen Kilometer.
              <br />
              (2) Für An- und Abfahrt zur Besichtigung zur Reparaturbestätigung berechnet der AN maximal 83,00 € netto (98,77 € brutto inkl. {MEHRWERTSTEUER}%
              gesetzlicher Mehrwertsteuer), sprich es werden maximal 100 km kostenmäßig berücksichtigt (Deckelung). Alle darüber hinausgehenden etwaigen
              Fahrtkosten für die Besichtigung zur Reparaturbestätigung sind hiermit abgegolten.
            </Typography>
          </Grid>
          <Grid size={12}>
            <Signature
              onSign={() => {}}
              onDeleteSignature={() => {}}
              signatureUrl={url}
              isLoading={isLoading}
              unterzeichnerAuswahl={vorgang?.unterschriften?.fahrtkostenvereinbarungReparaturnachweis?.unterzeichner ?? Unterzeichner.AUFTRAGGEBER}
              vorgang={vorgang}
              date={signDate}
              readOnly={true}
            />
          </Grid>
        </Grid>
      }
      footer={
        <div className="unterschriften__modal-button">
          <Button color="primary" variant="contained" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    />
  );
}
